import type { PersonAssignmentRetrieveType } from "../../../api/assignments/person-assignment";
import type { Comment } from "../../../api/comments/comments";
import type { Cooldown } from "../../../api/cooldown";
import type { PersonEmailQueueResponse } from "../../../api/messaging";
import type { LinkedinMessage } from "../../../api/messaging/linkedin-message";
import type { PersonActivity } from "../../../api/person/activity";
import type { PersonConversation } from "../../../api/person/conversations";
import type { PersonEmailMessage } from "../../../api/person/emails";
import type { PersonEvent } from "../../../api/person/events";
import type { PersonAttachment } from "../../../api/person/person-attachment";
import type { PersonVote } from "../../../api/votes/votes";
import type { KeyValueChipColor } from "../../common/KeyValueChip";

// Timeline entry types - discriminated union for all activity types
export type ActivityEntry =
	| {
			type: "email_queued";
			date: string;
			data: PersonEmailQueueResponse;
	  }
	| {
			type: "email_sent";
			date: string;
			data: PersonEmailQueueResponse;
	  }
	| {
			type: "linkedin_queued";
			date: string;
			data: LinkedinMessage & { sent: boolean };
	  }
	| {
			type: "linkedin_sent";
			date: string;
			data: LinkedinMessage & { sent: boolean };
	  }
	| {
			type: "linkedin_conversation";
			date: string;
			direction: "inbound" | "outbound";
			conversationUsername: string;
			conversationUserFirstName?: string;
			conversationShortUrn: string;
			conversationType: string;
			personName: string | null;
			data: PersonConversation;
	  }
	| {
			type: "email_journal";
			date: string;
			direction: "inbound" | "outbound";
			data: PersonEmailMessage;
	  }
	| {
			type: "vote";
			date: string;
			data: PersonVote;
	  }
	| {
			type: "event";
			date: string;
			data: PersonEvent;
	  }
	| {
			type: "board_movement";
			date: string;
			data: PersonAssignmentRetrieveType;
			fromColumn: string | null;
	  }
	| {
			type: "note";
			date: string;
			data: Comment;
	  }
	| {
			type: "attachment";
			date: string;
			data: PersonAttachment;
	  }
	| {
			type: "call";
			date: string;
			data: PersonActivity;
	  }
	| {
			type: "meeting";
			date: string;
			data: PersonActivity;
	  }
	| {
			type: "cooldown";
			date: string;
			data: Cooldown;
	  };

// Filter types for timeline
export type ActivityFilter =
	| "all"
	| "notes"
	| "attachments"
	| "emails"
	| "linkedin"
	| "calls"
	| "meetings"
	| "votes"
	| "board"
	| "cooldowns";

// Filter options configuration
export const FILTER_OPTIONS: {
	id: ActivityFilter;
	label: string;
	color: KeyValueChipColor;
}[] = [
	{ id: "all", label: "All", color: "primary" },
	{ id: "notes", label: "Notes", color: "warning" },
	{ id: "attachments", label: "Attachments", color: "warning" },
	{ id: "emails", label: "Emails", color: "success" },
	{ id: "linkedin", label: "LinkedIn", color: "primary" },
	{ id: "calls", label: "Calls", color: "info" },
	{ id: "meetings", label: "Meetings", color: "secondary" },
	{ id: "votes", label: "Votes", color: "secondary" },
	{ id: "board", label: "Board", color: "board" },
	{ id: "cooldowns", label: "Cooldowns", color: "info" },
];
